import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { subject } from '@casl/ability';
import { Button, BUTTON_TYPES, Tooltip, User, AtsSkeleton, Typography } from '_atoms';
import { ReactComponent as Info } from 'dist/icons/info.svg';
import { ACTIONS, Can, UI } from 'permission';
import { getApprovers, getSteps, setApprover } from 'actions';
import { useAppDispatch } from 'store';
import './style.scss';
import type { JobApprover } from 'store/reducers/jobReducer';
import type { ResponsibleRecruiter } from 'store/reducers/candidateReducer';
import { PopupEditApprovers } from '../PopupEditApprovers';

interface ApproversProps {
  list: Array<JobApprover & { optional?: boolean }>;
  loading: boolean;
  requestOptions: Record<string, unknown>;
  defaultApprover?: boolean;
  responsibleRecruiter?: ResponsibleRecruiter;
  rejected?: boolean;
}

export const Approvers: React.FC<ApproversProps> = ({
  list,
  loading,
  requestOptions,
  defaultApprover,
  responsibleRecruiter,
  rejected,
}) => {
  const { t } = useTranslation();
  const params = useParams<{ id: string; step: string; candidate: string }>();
  const dispatch = useAppDispatch();
  const [openPopup, setOpenPopup] = useState(false);
  const onCloseEditPopup = useCallback(() => setOpenPopup(false), []);
  const openEdipApprovers = () => setOpenPopup(true);

  const saveApprovers = (approvers: Array<{ user: string; optional?: boolean }>) => {
    setApprover(params.id, params.step, params.candidate, approvers)
      .then(() => {
        dispatch(getSteps(params.id, true));
        dispatch(getApprovers(params.id, params.step, params.candidate));
      })
      .finally(onCloseEditPopup);
  };
  return (
    <div className="approvers-list">
      <div className="approvers-list__header">
        <div className="title">
          <Typography.Title2>{t('translation:approvers.title')}</Typography.Title2>
          <Tooltip label={t('translation:approvers.tooltip_decision_people')}>
            <Info />
          </Tooltip>
        </div>
        <Can I={ACTIONS.READ} this={subject(UI.EDIT_APPROVERS, { is_default: defaultApprover, rejected })}>
          <Button type={BUTTON_TYPES.GHOST} onClick={openEdipApprovers} disabled={loading}>
            {t('translation:approvers.button_edit_approvers')}
          </Button>
        </Can>
      </div>
      <div className="approvers-list__content">
        {loading ? (
          <AtsSkeleton height={84} width={704} />
        ) : (
          list.map(el => (
            <User
              key={el.uuid}
              fullName={
                <>
                  {el.user.full_name}
                  {el.optional && <span className="optional-tag">{t('translation:approvers.text_optional')}</span>}
                </>
              }
              avatar={el.user.profile?.avatar}
              roles={el.user.roles}
            />
          ))
        )}
      </div>
      <PopupEditApprovers
        open={openPopup}
        onClose={onCloseEditPopup}
        onSave={saveApprovers}
        approvers={list}
        responsibleRecruiter={responsibleRecruiter}
        requestOptions={requestOptions}
      />
    </div>
  );
};
